import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Sparkles } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

const timeAgo = (iso) => {
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 60) return "just now";
  if (d < 3600) return `${Math.floor(d / 60)}m ago`;
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  return `${Math.floor(d / 86400)}d ago`;
};

export const NotificationBell = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const ref = useRef(null);

  const isPro = user?.role === "professional";

  const loadCount = useCallback(() => {
    if (!isPro) return;
    api.get("/notifications/unread-count").then((r) => setCount(r.data.count || 0)).catch(() => {});
  }, [isPro]);

  useEffect(() => {
    if (!isPro) return;
    loadCount();
    const t = setInterval(loadCount, 30000);
    return () => clearInterval(t);
  }, [isPro, loadCount]);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next) {
      const r = await api.get("/notifications").catch(() => ({ data: [] }));
      setItems(r.data || []);
      if (count > 0) {
        await api.post("/notifications/read-all").catch(() => {});
        setCount(0);
      }
    }
  };

  const openJob = (n) => {
    setOpen(false);
    navigate("/opportunities");
  };

  if (!isPro) return null;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={toggle}
        data-testid="notification-bell"
        aria-label="Notifications"
        className="relative flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 transition-colors hover:bg-slate-50"
      >
        <Bell className="h-5 w-5" />
        {count > 0 && (
          <span data-testid="notification-badge" className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-[#16A34A] px-1 text-[11px] font-bold text-white">
            {count > 9 ? "9+" : count}
          </span>
        )}
      </button>

      {open && (
        <div data-testid="notification-panel" className="absolute right-0 top-12 z-50 w-80 overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-xl">
          <div className="flex items-center gap-2 border-b border-slate-100 px-4 py-3">
            <Sparkles className="h-4 w-4 text-[#16A34A]" />
            <span className="font-heading text-sm font-bold text-[#0B2545]">Matches for you</span>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <div className="px-4 py-10 text-center text-sm text-slate-500">No matches yet. We'll alert you when a new opening fits your qualification and location.</div>
            ) : (
              items.map((n) => (
                <button
                  key={n.id}
                  onClick={() => openJob(n)}
                  data-testid="notification-item"
                  className={`block w-full border-b border-slate-50 px-4 py-3 text-left transition-colors hover:bg-slate-50 ${n.read ? "" : "bg-[#16A34A]/5"}`}
                >
                  <p className="text-sm font-semibold text-[#0B2545]">{n.title}</p>
                  <p className="mt-0.5 line-clamp-2 text-xs text-slate-600">{n.body}</p>
                  <p className="mt-1 text-[11px] text-slate-400">{timeAgo(n.created_at)}</p>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
